/**
 * The browser's view of the API. The transport and the token source are
 * injected: production uses fetch and Firebase; tests route into the real
 * API in memory. Every failure is an ApiError with the status and the
 * server's message, so pages can show it as it is.
 */
export type HttpLike = (url: string, init: { method: string; headers: Record<string, string>; body?: string }) => Promise<{ status: number; text(): Promise<string> }>;

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly detail: string, public readonly body: unknown) {
    super(detail || `HTTP ${status}`);
    this.name = 'ApiError';
  }
}

export interface Claims { orgId: string; role: 'admin' | 'editor' | 'viewer'; plan: string; periodEnd: number; claimsVersion: number; }
export interface Me { org: { id: string; status: string; plan: string; periodEnd: number }; me: { uid: string; role: Claims['role'] }; }
export interface Board { id: string; name: string; archived: boolean; createdAt: number; updatedAt: number; }
export interface Member { uid: string; role: Claims['role']; email?: string; name?: string; }
export interface Invite { code: string; email: string; role: Claims['role']; expiresAt: number; }
export interface Machine { machineId: string; name: string; capabilities: string[]; lastSeen: number | null; }
export type CutPhase = 'animatic' | 'fix-animatic' | 'final' | 'fix-final';
export interface ReviewShort {
  slot: number; title: string; releaseDate: string | null; slug: string | null; state: string; phase: CutPhase | null; phaseStartedAt: string | null;
  reviewVersion: number | null; costUsd: number; lastError: string | null; jobId: string | null;
  review: { bucketKey: string | null; file: string; bytes: number; md5: string; phase: CutPhase; at: string; notes: string | null; questions: string | null } | null;
  steps: Array<{ state: string; at: string }>;
}
export interface ReviewJob { boardId: string; boardName: string; taskId: string; state: string; costUsd: number; updatedAt: string; shorts: Array<Pick<ReviewShort, 'slot' | 'title' | 'state' | 'phase' | 'reviewVersion'>>; }
export interface ReviewDecision { decision: 'approved' | 'changes_requested' | 'retry'; notes?: Array<{ text: string }>; }
export interface PricingPlan { id: string; name: string; machineLimit: number | null; creditsIncluded: number; storageGb: number; trialDays: number | null; dedicated: boolean; priceId?: string; amount?: number | null; currency?: string; interval?: 'month' | 'year' | null; }
export interface Pricing { configured: boolean; publishableKey?: string; checkoutMode?: 'embedded' | 'hosted'; plans: PricingPlan[]; packs: Array<{ credits: number; priceId: string; amount?: number | null; currency?: string }>; }
export interface LedgerView { periodId: string; periodEnd: number; allowance: number; purchased: number; spent: number; reserved: number; remaining: number; low: boolean; }

export class WebApi {
  constructor(private readonly http: HttpLike, private readonly token: () => Promise<string | null>, private readonly baseUrl = '') {}

  private async call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const t = await this.token();
    const headers: Record<string, string> = { accept: 'application/json' };
    if (t) headers.authorization = `Bearer ${t}`;
    if (body !== undefined) headers['content-type'] = 'application/json';
    let res: Awaited<ReturnType<HttpLike>>;
    try { res = await this.http(`${this.baseUrl}${path}`, { method, headers, ...(body !== undefined ? { body: JSON.stringify(body) } : {}) }); }
    catch (e) { throw new ApiError(0, 'The server could not be reached.', null); }
    const text = await res.text();
    let parsed: unknown = null;
    if (text) { try { parsed = JSON.parse(text); } catch { parsed = text; } }
    if (res.status >= 400) {
      const detail = parsed && typeof parsed === 'object' && typeof (parsed as { error?: unknown }).error === 'string' ? (parsed as { error: string }).error : `HTTP ${res.status}`;
      throw new ApiError(res.status, detail, parsed);
    }
    return parsed as T;
  }

  me = () => this.call<Me>('GET', '/api/orgs/me');
  bootstrap = () => this.call<{ claims: Claims }>('POST', '/api/auth/bootstrap');
  acceptInvite = (code: string) => this.call<{ claims: Claims; member: Member & { orgId: string } }>('POST', '/api/auth/accept-invite', { code });
  /** Start a trial workspace for the signed-in person (a verified address, no workspace yet). */
  createOrg = () => this.call<{ claims: Claims; org: { id: string; status: string; plan: string; periodEnd: number } }>('POST', '/api/auth/orgs');
  ledger = () => this.call<LedgerView>('GET', '/api/ledger');
  /** Public. */
  pricing = () => this.call<Pricing>('GET', '/api/pricing');
  billing = {
    checkout: (body: { plan: string } | { credits: number }) => this.call<{ sessionId: string; mode: 'embedded' | 'hosted'; clientSecret?: string; url?: string }>('POST', '/api/billing/checkout', body),
    portal: () => this.call<{ url: string }>('POST', '/api/billing/portal').then((r) => r.url),
  };
  boards = {
    list: () => this.call<{ boards: Board[] }>('GET', '/api/boards').then((r) => r.boards),
    create: (name: string) => this.call<{ board: Board; room: string }>('POST', '/api/boards', { name }),
    get: (id: string) => this.call<{ board: Board; room: string }>('GET', `/api/boards/${encodeURIComponent(id)}`),
    update: (id: string, patch: { name?: string; archived?: boolean }) => this.call<{ board: Board }>('PATCH', `/api/boards/${encodeURIComponent(id)}`, patch).then((r) => r.board),
  };
  members = {
    list: () => this.call<{ members: Member[]; invites?: Invite[] }>('GET', '/api/members'),
    invite: (email: string, role: Claims['role']) => this.call<Invite>('POST', '/api/members/invites', { email, role }),
    revokeInvite: (code: string) => this.call<null>('POST', `/api/members/invites/${encodeURIComponent(code)}/revoke`),
    setRole: (uid: string, role: Claims['role']) => this.call<{ member: Member }>('PATCH', `/api/members/${encodeURIComponent(uid)}`, { role }).then((r) => r.member),
    remove: (uid: string) => this.call<null>('DELETE', `/api/members/${encodeURIComponent(uid)}`),
  };
  review = {
    list: () => this.call<{ jobs: ReviewJob[] }>('GET', '/api/review').then((r) => r.jobs),
    shorts: (boardId: string) => this.call<{ taskId: string; state: string; shorts: ReviewShort[]; costUsd: number }>('GET', `/api/boards/${encodeURIComponent(boardId)}/shorts`),
    decide: (boardId: string, slot: number, body: ReviewDecision) => this.call<{ ok: true; short: ReviewShort; jobId: string | null; taskDone: boolean }>('POST', `/api/boards/${encodeURIComponent(boardId)}/shorts/${slot}/review`, body),
  };
  media = {
    /** A short-lived URL for an object in the workspace bucket; 501 when the workspace has none. */
    objectUrl: (key: string) => this.call<{ key: string; url: string; expiresAt: number }>('GET', `/api/media/objects/${key.split('/').map(encodeURIComponent).join('/')}`).then((r) => r.url),
  };
  machines = {
    list: () => this.call<{ machines: Machine[] }>('GET', '/api/machines').then((r) => r.machines),
    enrolCode: () => this.call<{ code: string; expiresAt: number }>('POST', '/api/machines/enrol-codes'),
    revoke: (id: string) => this.call<null>('DELETE', `/api/machines/${encodeURIComponent(id)}`),
  };
}

/** The production transport. */
export const browserHttp: HttpLike = async (url, init) => { const r = await fetch(url, init); return { status: r.status, text: () => r.text() }; };
